// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.

const NA_VALUES = new Set([undefined, null, "NA", ""]);

/**
 * Get a formatted display value for a column from a raw data row.
 * Uses the parsed config's column formatter when available,
 * falling back to plain string conversion.
 *
 * @param {Record<string, any> | undefined} row - The raw data row
 * @param {string} col - Column name
 * @param {object} [config] - Parsed config object (with .columns Map)
 * @returns {string}
 */
export function getDisplayValue(row, col, config) {
  if (!row) return "";
  const val = row[col];
  if (NA_VALUES.has(val)) return "N/A";

  const colConfig = config?.columns?.get(col);
  if (colConfig?.format) {
    // Numeric formatters expect a number; try to coerce
    const num = Number(val);
    if (!isNaN(num)) return colConfig.format(num);
  }

  return String(val);
}

/**
 * Parse a boolean field from a raw data row.
 * @param {Record<string, any> | undefined} row
 * @param {string} col
 * @returns {boolean | null}
 */
export function getBooleanValue(row, col) {
  if (!row) return null;
  const val = row[col];
  if (val === true || val === "TRUE" || val === "true") return true;
  if (val === false || val === "FALSE" || val === "false") return false;
  return null;
}

/**
 * @typedef {{ name: string, description: string }} ProgramInfo
 */

/**
 * Extract structured program info from the parsed _name/_description columns.
 * @param {Record<string, any> | undefined} row
 * @param {{ name: string, description: string }[]} programCols
 * @returns {ProgramInfo[]}
 */
export function getPrograms(row, programCols) {
  if (!row) return [];
  return programCols
    .map((cols) => ({
      name: row[cols.name],
      description: row[cols.description],
    }))
    .filter((p) => p.name && !NA_VALUES.has(p.name));
}
